import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Chip,
    Stack,
    Paper
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    Schedule as ScheduleIcon,
    Settings as SettingsIcon,
    BarChart as BarChartIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Block as BlockIcon
} from '@mui/icons-material';

export default function BroadcastGuide({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
                🎉 Система рассылок - Руководство
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Stack spacing={3}>
                    <Alert severity="info">
                        Мощная система массовых рассылок в Telegram с автоматизацией и детальной аналитикой
                    </Alert>

                    {/* Основные возможности */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            ✨ Основные возможности
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><RefreshIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="Автоматические периодические рассылки"
                                    secondary="Настройте один раз - повторяется автоматически каждые N минут/часов/дней/недель/месяцев"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><FilterIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="Умная фильтрация получателей"
                                    secondary="По статусу, балансу, заказам, активности - отправляйте только нужной аудитории"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ScheduleIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="Тонкая настройка времени"
                                    secondary="Задержки между сообщениями, временной диапазон отправки, защита от блокировок"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><BarChartIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="Детальная аналитика"
                                    secondary="Прогресс в реальном времени, логи каждой отправки, сводка ошибок"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="Редактирование и повтор"
                                    secondary="Изменяйте любую рассылку и запускайте заново"
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Divider />

                    {/* Как начать */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            🚀 Как начать?
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="body2" paragraph>
                                <strong>Шаг 1:</strong> Нажмите "Создать рассылку"
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>Шаг 2:</strong> Заполните название и текст сообщения
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>Шаг 3:</strong> Выберите ботов (кнопка "Выбрать всех" для быстрого выбора)
                            </Typography>
                            <Typography variant="body2" paragraph>
                                <strong>Шаг 4:</strong> Настройте фильтры получателей (опционально)
                            </Typography>
                            <Typography variant="body2">
                                <strong>Шаг 5:</strong> Нажмите "Создать рассылку" и наблюдайте за прогрессом!
                            </Typography>
                        </Paper>
                    </Box>

                    <Divider />

                    {/* Примеры */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            💡 Примеры использования
                        </Typography>
                        
                        <Stack spacing={2}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    📅 Ежедневная утренняя рассылка
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Периодическая: Каждые 1 день<br/>
                                    • Запланировать: Завтра 09:00<br/>
                                    • Текст: "☀️ Доброе утро! Желаем отличного дня!"
                                </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    🎯 VIP предложение
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Фильтры: Только активные<br/>
                                    • Минимальный баланс: 5000₽<br/>
                                    • Наличие заказов: Есть заказы
                                </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    ⏰ Напоминание каждые 3 часа
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Периодическая: Каждые 3 часа<br/>
                                    • Остановить после: 8 повторений<br/>
                                    • Текст: "⏰ Акция заканчивается!"
                                </Typography>
                            </Paper>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Статусы и символы */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            📊 Понимание прогресса
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                            Статусы рассылок:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            <Chip label="🕐 Ожидает" size="small" />
                            <Chip label="▶️ Выполняется" size="small" color="primary" />
                            <Chip label="✅ Завершена" size="small" color="success" />
                            <Chip label="❌ Ошибка" size="small" color="error" />
                            <Chip label="🚫 Отменена" size="small" color="warning" />
                        </Stack>

                        <Typography variant="subtitle2" gutterBottom>
                            Символы прогресса:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText
                                    primary="✓ Отправлено"
                                    secondary="Успешно доставленные сообщения"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                                <ListItemText
                                    primary="✗ Ошибки"
                                    secondary="Неудачные отправки (бот заблокирован, чат не найден)"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><BlockIcon /></ListItemIcon>
                                <ListItemText
                                    primary="⊘ Пропущено"
                                    secondary="Пользователи заблокированы в системе или бот не найден"
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Divider />

                    {/* Советы */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            💡 Полезные советы
                        </Typography>
                        <Alert severity="success" sx={{ mb: 1 }}>
                            <Typography variant="body2">
                                <strong>Тестируйте:</strong> Сначала создайте рассылку с жесткими фильтрами на малую группу
                            </Typography>
                        </Alert>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            <Typography variant="body2">
                                <strong>Время отправки:</strong> Используйте временной диапазон 9:00-21:00 для лучших результатов
                            </Typography>
                        </Alert>
                        <Alert severity="warning">
                            <Typography variant="body2">
                                <strong>Не спамьте:</strong> Используйте фильтры для таргетированных рассылок, не отправляйте всем подряд
                            </Typography>
                        </Alert>
                    </Box>

                    <Divider />

                    {/* Дополнительные возможности */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            🎁 Дополнительные возможности
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText
                                    primary="🤖 Множественные боты"
                                    secondary='Отправляйте через несколько ботов, используйте кнопку "Выбрать всех"'
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="🛡️ Защита от блокировок"
                                    secondary="Автоматические задержки и обработка rate limit"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="📝 Детальные логи"
                                    secondary="Запись каждой попытки с информацией о получателе и причине ошибки"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="🔄 Редактирование"
                                    secondary="Измените любую рассылку (кроме выполняющейся) и запустите заново"
                                />
                            </ListItem>
                        </List>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" size="large">
                    Понятно, начинаем!
                </Button>
            </DialogActions>
        </Dialog>
    );
}
